import React, { useEffect } from "react";
import { makeFilterUsersWithNoFlowFolders } from "../../reducers/_folderGeneral";
import { useDispatch, useSelector } from "react-redux";
import FlowList from "./FlowList";
import FlowFolderContextMenu from "./FlowFolderContextMenu";
import FlowTreeContextMenu from "./FlowTreeContextMenu";
import withUpdateStripes from "../misc/withUpdateStripes";
import { sortTopLevelFolders } from "../../helpers/folder";
import withScrolling, { createVerticalStrength, createHorizontalStrength } from "react-dnd-scrolling";
import { Folder } from "../../types/shared";
import { IAppState } from "../../types/stores";
import { getTreeSearch } from "../../reducers/search";
import { requestFlows } from "../../actions/flowActions";

const Scrollzone = withScrolling("div");
const vStrength = createVerticalStrength(150);
const hStrength = createHorizontalStrength(150);

const selectFlowTreeFolders = makeFilterUsersWithNoFlowFolders();

type Props = {
    // Passed In
    folderType?: string;
    schedulesMode?: boolean;
    /** When true, do not bulk-fetch all flows when global tree search text is non-empty (flow design uses name overlay instead). */
    disableTreeSearchBulkFlowFetch?: boolean;
    // From withUpdateStripes HOC
    updateStripes?: () => void;
};

const FlowTree: React.FC<Props> = ({
    updateStripes,
    schedulesMode,
    folderType = "W",
    disableTreeSearchBulkFlowFetch = false,
}) => {
    const dispatch = useDispatch();
    const flowFolders = useSelector(state => selectFlowTreeFolders(state, { folderType }));

    const userNameForId = useSelector((state: IAppState) => state.varsPersist.userNameForId);
    const userId = useSelector((state: IAppState) => state.session.userId!);
    const companyId = useSelector((state: IAppState) => state.session.companyId!);
    const enabledFeatures = useSelector((state: IAppState) => state.session.enabledFeatures);
    const treeSearch = useSelector((state: IAppState) => getTreeSearch(state));
    const lazyFlowTree = enabledFeatures.includes("fsd-lazy-loading-flowtree");

    useEffect(() => {
        updateStripes && updateStripes();
    }, [updateStripes]);

    useEffect(() => {
        if (disableTreeSearchBulkFlowFetch) {
            return;
        }
        // In lazy tree mode, searching should fetch all flows once like legacy behavior.
        if (!lazyFlowTree) {
            return;
        }
        if (treeSearch == null || treeSearch === "") {
            return;
        }
        dispatch(requestFlows(null, "replace", true));
    }, [dispatch, disableTreeSearchBulkFlowFetch, lazyFlowTree, treeSearch]);

    let folders = sortTopLevelFolders(flowFolders, userNameForId, userId);
    if (schedulesMode && !enabledFeatures.includes("schedules-admin")) {
        // Only show folders that belong to the user
        folders = folders.filter(f => f.userId == userId || (f.isHistorical && (f.userId == 0 || f.userId == userId)));
    }

    const topLevelFolder: Folder = {
        folderType,
        children: folders,
        name: "",
        id: 0,
        order: 1,
        userId,
        companyId,
        isCompanyShare: false,
        level: -1,
        isInternalShare: false,
        isHistorical: false,
    };

    return (
        <Scrollzone className="scrollyrolly react_flows" verticalStrength={vStrength} horizontalStrength={hStrength}>
            <FlowList
                folderType={folderType}
                folder={topLevelFolder}
                treeViewOnUpdate={updateStripes}
                showAllDuringSearch={false}
                schedulesMode={schedulesMode}
            />
            <FlowFolderContextMenu />
            <FlowTreeContextMenu />
        </Scrollzone>
    );
};
export default withUpdateStripes(FlowTree);
